import type { IntlShape } from "react-intl";
import { type ZodTypeAny, z } from "zod";
import type InputType from "../../Input/InputType";
import { JsonInputType } from "../../Input/JsonInputType";
import { SideName } from "../../Side/SideName";
import Plate from "../Plate";

export default class Drop extends Plate {
	public static code = "plate_drop";

	public getDimensionsString(input: InputType, intl: IntlShape) {
		const { length } = this.formatInputs(input, intl, "decimal");
		const { width } = this.formatInputs(input, intl);
		if (length && width) {
			return `${length} x ø ${width}`;
		}

		return undefined;
	}

	public calculateSideLength(side: SideName, input: InputType) {
		const length = Number(input[JsonInputType.Length]);
		const diameter = Number(input[JsonInputType.Width]);

		if (!(Number.isNaN(length) || Number.isNaN(diameter))) {
			switch (side) {
				case SideName.Length1:
					return this.calculateOutlineLength(length, diameter);
			}
		}

		return undefined;
	}

	protected calculateMaxBeltSize(data: Record<JsonInputType, number>) {
		const r = data.width / 2;
		const th = data.length - data.width;

		return super.calculateMaxBeltSize({
			[JsonInputType.Length]: data.width / 2 + Math.sqrt((th + r) ** 2 - r ** 2),
			[JsonInputType.Width]: data.width,
		} as Record<JsonInputType, number>);
	}

	protected getDimensionSchema(): ZodTypeAny {
		const inputs = super.getDimensionSchema();

		return inputs.superRefine((data, context) => {
			const minLength = data.width * 1.25;

			if (data.length < minLength) {
				context.addIssue({
					code: z.ZodIssueCode.custom,
					params: {
						type: "difference_two_fields_minimum",
						field_1: [this.getInputById(JsonInputType.Length)?.label],
						field_2: [this.getInputById(JsonInputType.Width)?.label],
						minimum: minLength - data.width,
					},
					path: ["length"],
				});
			}
		});
	}

	protected calculateOutlineLength(length: number, diameter: number): number {
		const r = diameter / 2;
		const th = length - diameter;
		const tl = Math.sqrt((th + r) ** 2 - r ** 2);
		const p = r ** 2 / (th + r);
		const hc = p + r;
		const al = r * 2 * Math.acos(1 - hc / r);

		return al + 2 * tl;
	}
}
